import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShoppingBag, Plus, Minus, Trash2, Phone, Star, MessageCircle, Edit3, Store as StoreIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import FloatingSlot from "@/components/FloatingSlot";

const rupees = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN")}`;

export default function Storefront() {
  const { key: rawKey } = useParams();
  const { user } = useAuth();
  const key = rawKey === "me" ? (user?.user_id || "") : rawKey;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState([0, 100000]);
  const [cart, setCart] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/catalog/${key}`);
      setData(r.data);
      const prices = (r.data.items || []).map(i => i.price_paise / 100);
      if (prices.length) setRange([0, Math.max(...prices)]);
    } catch { toast.error("Storefront not found"); }
    finally { setLoading(false); }
  };
  const loadCart = async () => {
    if (!user) return;
    try { const r = await api.get("/commerce/cart"); setCart(r.data); } catch {}
  };
  useEffect(() => { if (key) load(); }, [key]);
  useEffect(() => { loadCart(); }, [user]);

  const isOwner = user && data?.seller && data.seller.user_id === user.user_id;

  const filteredItems = useMemo(() => {
    if (!data) return [];
    return data.items.filter(i => {
      const p = (i.price_paise || 0) / 100;
      return p >= range[0] && p <= range[1];
    });
  }, [data, range]);

  const addToCart = async (item) => {
    if (!user) { toast.error("Sign in to buy"); return; }
    try {
      await api.post("/commerce/cart/add", { item_id: item.item_id, qty: 1 });
      loadCart();
      toast.success(`Added ${item.name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="inline animate-spin" /></div>;
  if (!data) return <div className="p-12 text-center text-stone-500">Store not found.</div>;

  const s = data.seller;
  const rating = data.rating;
  const maxPrice = Math.max(1000, ...(data.items || []).map(i => (i.price_paise || 0) / 100));

  return (
    <div className="max-w-5xl mx-auto px-4 py-4" data-testid="storefront">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#2D5A27] to-[#1a3e17] text-white rounded-3xl p-5 md:p-6">
        <div className="flex items-start gap-4">
          {s.picture ? <img loading="lazy" src={s.picture} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-white/30" />
            : <div className="w-16 h-16 rounded-full bg-white/20 grid place-items-center font-black text-2xl">{s.name?.[0] || "S"}</div>}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-200">Store</div>
            <div className="heading text-2xl md:text-3xl font-black truncate">{s.name}</div>
            {s.nvn && <div className="font-mono text-xs text-emerald-200 mt-0.5">NVN {s.nvn}</div>}
            <div className="flex items-center gap-3 mt-2 text-xs">
              <span className="inline-flex items-center gap-1"><Star size={12} fill="currentColor" /> {rating.avg} ({rating.count})</span>
              <span>{data.items.length} items</span>
              {!isOwner && s.mobile && <a href={`tel:${s.mobile}`} className="inline-flex items-center gap-1 hover:underline"><Phone size={11} /> Call</a>}
              {!isOwner && <Link to={`/messenger`} className="inline-flex items-center gap-1 hover:underline"><MessageCircle size={11} /> Message</Link>}
            </div>
          </div>
          {isOwner && (
            <Button onClick={() => setEditorOpen(true)} variant="outline" className="rounded-full bg-white/10 text-white border-white/30 hover:bg-white/20" data-testid="store-add-item">
              <Plus size={14} className="mr-1" /> Add item
            </Button>
          )}
        </div>
      </div>

      {/* Price filter */}
      {data.items.length > 0 && (
        <div className="mt-4 flex items-center gap-3 bg-white border border-stone-200 rounded-2xl p-4">
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 shrink-0">Price</div>
          <div className="flex-1 flex items-center gap-2">
            <span className="text-xs font-mono">₹{range[0]}</span>
            <input
              type="range" min="0" max={maxPrice} value={range[1]}
              onChange={e => setRange([range[0], +e.target.value])}
              className="flex-1 accent-[#E05E36]"
              data-testid="store-price-slider"
            />
            <span className="text-xs font-mono">₹{range[1]}</span>
          </div>
        </div>
      )}

      {/* Items grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
        {filteredItems.length === 0 ? (
          <div className="col-span-full text-center py-12 text-stone-500">
            <StoreIcon className="mx-auto mb-2 text-stone-300" size={36} />
            No items in this price range.
          </div>
        ) : filteredItems.map(it => (
          <ItemCard key={it.item_id} item={it} onAdd={() => addToCart(it)} onBuy={() => addToCart(it)} onRefresh={load} isOwner={isOwner} />
        ))}
      </div>

      {/* Floating cart button — bottom-right stack, order 2 (above AINA + voice mic). */}
      {cart?.summary?.item_count > 0 && (
        <FloatingSlot zone="bottom-right" order={2} size="fab" zIndex="fab-secondary" hideOnKeyboard={false}>
          <Link to="/cart" className="bg-[#E05E36] text-white rounded-full shadow-2xl px-4 py-3 flex items-center gap-2 font-bold" data-testid="cart-fab">
            <ShoppingBag size={18} /> {cart.summary.item_count} · {rupees(cart.summary.subtotal_paise)}
          </Link>
        </FloatingSlot>
      )}

      {editorOpen && <ItemEditor onClose={() => setEditorOpen(false)} onSaved={() => { setEditorOpen(false); load(); }} />}
    </div>
  );
}

function ItemCard({ item, onAdd, onBuy, onRefresh, isOwner }) {
  const rating = item.rating_avg || 0;
  const isContact = item.buy_type === "contact";

  return (
    <motion.div
      whileHover={{ y: -3 }}
      className="bg-white rounded-2xl border border-stone-200 overflow-hidden flex flex-col"
      data-testid={`item-card-${item.item_id}`}
    >
      {item.image
        ? <img loading="lazy" src={item.image} alt="" className="w-full h-40 object-cover" />
        : <div className="w-full h-40 bg-stone-100 grid place-items-center text-stone-300"><ShoppingBag size={40} /></div>}
      <div className="p-3 flex-1 flex flex-col">
        <div className="font-bold text-sm text-stone-900 line-clamp-1">{item.name}</div>
        {item.description && <div className="text-xs text-stone-500 line-clamp-2 mt-0.5">{item.description}</div>}
        <div className="mt-2 flex items-center justify-between">
          <div className="text-lg font-black text-[#E05E36]">{rupees(item.price_paise)}</div>
          {rating > 0 && <div className="text-[11px] text-amber-600 font-bold inline-flex items-center gap-0.5"><Star size={10} fill="currentColor" />{rating}</div>}
        </div>
        <div className="mt-2 flex gap-1.5">
          {isContact ? (
            <a href={`tel:${item.seller_id}`} className="flex-1 text-center text-xs font-bold py-2 rounded-full bg-emerald-500 text-white">Contact</a>
          ) : (
            <>
              <button onClick={onAdd} className="flex-1 text-xs font-bold py-2 rounded-full bg-stone-100 hover:bg-stone-200" data-testid={`add-cart-${item.item_id}`}>
                Add
              </button>
              <button onClick={onBuy} className="flex-1 text-xs font-bold py-2 rounded-full bg-[#E05E36] text-white" data-testid={`buy-${item.item_id}`}>
                Buy
              </button>
            </>
          )}
          {isOwner && (
            <button
              onClick={async () => {
                if (!window.confirm("Remove this item?")) return;
                try { await api.delete(`/catalog/item/${item.item_id}`); onRefresh(); toast.success("Removed"); }
                catch { toast.error("Failed"); }
              }}
              className="text-red-500 p-2 rounded-full hover:bg-red-50"
              data-testid={`del-item-${item.item_id}`}
            ><Trash2 size={14} /></button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function ItemEditor({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "", price_paise: 0, image: null, stock: null, buy_type: "fixed" });
  const [saving, setSaving] = useState(false);

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setForm({ ...form, image: r.result });
    r.readAsDataURL(f);
  };

  const save = async () => {
    if (!form.name.trim() || form.price_paise <= 0) { toast.error("Name + price required"); return; }
    setSaving(true);
    try {
      await api.post("/catalog/item", form);
      toast.success("Item added");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-5 space-y-3" data-testid="item-editor">
        <div className="font-bold text-lg">Add item</div>
        <Input placeholder="Item name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="item-name" />
        <Textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" placeholder="Price (₹)" onChange={e => setForm({ ...form, price_paise: Math.round((+e.target.value || 0) * 100) })} data-testid="item-price" />
          <Input type="number" placeholder="Stock (optional)" onChange={e => setForm({ ...form, stock: +e.target.value || null })} />
        </div>
        <label className="block text-xs font-bold text-stone-500">Image
          <input type="file" accept="image/*" onChange={pickImage} className="mt-1 block w-full text-xs" data-testid="item-image" />
        </label>
        {form.image && <img loading="lazy" src={form.image} alt="" className="w-full h-32 object-cover rounded-xl" />}
        <select value={form.buy_type} onChange={e => setForm({ ...form, buy_type: e.target.value })} className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm">
          <option value="fixed">Direct checkout</option>
          <option value="contact">Contact seller before buying</option>
        </select>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="item-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
